import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  CheckCircle, XCircle, FileText, AlertTriangle, Pencil, Save,
  RotateCcw, MessageSquare, Clock, Loader2, Download, Eye, Upload
} from 'lucide-react';

const API_BASE = window.location.origin;

const CONFIDENCE_BADGES = {
  very_high: 'bg-green-50 border-green-300 text-green-700',
  high: 'bg-green-50 border-green-300 text-green-700',
  medium: 'bg-amber-50 border-amber-300 text-amber-700',
  low: 'bg-red-50 border-red-300 text-red-700',
  very_low: 'bg-red-50 border-red-300 text-red-700',
};

function PdfPreview({ invoiceId }) {
  const [state, setState] = useState('loading');
  const [reuploading, setReuploading] = useState(false);
  const blobUrlRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE}/api/invoices/${invoiceId}/file`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error('File not available');
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setState(url);
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [invoiceId]);

  if (state === 'loading') {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="pdf-loading">
        <div className="text-center space-y-3">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#0A2463]" />
          <p className="text-xs text-slate-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    const handleReupload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setReuploading(true);
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/invoices/${invoiceId}/reupload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setState('loading');
        const token = localStorage.getItem('access_token');
        const r = await fetch(`${API_BASE}/api/invoices/${invoiceId}/file`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('failed');
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setState(url);
      } catch {
        setState('error');
      } finally {
        setReuploading(false);
      }
    };

    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="pdf-unavailable">
        <div className="w-full max-w-sm bg-white border border-slate-200 rounded-sm p-8 text-center space-y-3">
          <div className="w-12 h-12 mx-auto bg-slate-100 rounded-sm flex items-center justify-center">
            <FileText className="h-6 w-6 text-slate-300" />
          </div>
          <p className="text-xs font-medium text-slate-500">Document preview not available</p>
          <p className="text-[11px] text-slate-400">Re-upload the document to enable preview</p>
          <label
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463]/5 cursor-pointer transition-colors"
            data-testid="invoice-reupload-button"
          >
            {reuploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {reuploading ? 'Uploading...' : 'Re-upload Document'}
            <input type="file" accept=".pdf" className="hidden" onChange={handleReupload} disabled={reuploading} />
          </label>
        </div>
      </div>
    );
  }

  // state is a blob URL
  return (
    <div className="flex-1 flex flex-col" data-testid="pdf-preview">
      <iframe
        src={state}
        className="flex-1 w-full border-0"
        title="Invoice PDF Preview"
        data-testid="pdf-iframe"
      />
      <div className="px-3 py-1.5 border-t border-slate-200 flex items-center gap-2 bg-white">
        <Eye className="h-3 w-3 text-slate-400" />
        <span className="text-[10px] text-slate-400 flex-1">Inline document preview</span>
        <a
          href={state}
          download
          className="text-[10px] text-[#0A2463] hover:underline flex items-center gap-1"
          data-testid="pdf-download"
        >
          <Download className="h-3 w-3" /> Download
        </a>
      </div>
    </div>
  );
}

const EDITABLE_FIELDS = [
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'invoice_number', label: 'Invoice Number' },
  { key: 'invoice_date', label: 'Invoice Date' },
  { key: 'trade_id', label: 'Trade ID' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'currency', label: 'Currency' },
  { key: 'payment_terms', label: 'Payment Terms' },
  { key: 'vessel_name', label: 'Vessel Name' },
  { key: 'port_of_loading', label: 'Port of Loading' },
  { key: 'port_of_discharge', label: 'Port of Discharge' },
];

export default function ExtractionReviewPage() {
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);

  useEffect(() => {
    const loadReviewable = async () => {
      try {
        // Fetch invoices in all reviewable statuses
        const statuses = ['extracted', 'review_required', 'rejected_invalid_trade_id'];
        const allInvoices = [];
        for (const status of statuses) {
          const r = await api.get('/invoices', { params: { status } });
          allInvoices.push(...(r.data.invoices || []));
        }
        // Sort by most recent first
        allInvoices.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setInvoices(allInvoices);
        if (allInvoices.length > 0) loadInvoice(allInvoices[0].id);
        else setLoading(false);
      } catch {
        setLoading(false);
      }
    };
    loadReviewable();
  }, []);

  const loadInvoice = async (id) => {
    setLoading(true);
    setEditing({});
    setEditValues({});
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSelected(data);
      setFeedbackHistory(data.ai_feedback || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getFieldValue = (key) => {
    if (!selected) return '';
    const map = {
      vendor_name: selected.vendor_name,
      invoice_number: selected.invoice_number,
      invoice_date: selected.invoice_date,
      trade_id: selected.trade_id,
      total_amount: selected.total_amount,
      currency: selected.currency,
      payment_terms: selected.payment_terms,
      vessel_name: selected.vessel_name,
      port_of_loading: selected.port_loading,
      port_of_discharge: selected.port_discharge,
    };
    return map[key] || '';
  };

  const startEdit = (key) => {
    setEditing(prev => ({ ...prev, [key]: true }));
    setEditValues(prev => ({ ...prev, [key]: String(getFieldValue(key) || '') }));
  };

  const cancelEdit = (key) => {
    setEditing(prev => ({ ...prev, [key]: false }));
    setEditValues(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const saveCorrections = async () => {
    const corrections = Object.entries(editing)
      .filter(([, active]) => active)
      .map(([key]) => ({
        field_name: key,
        old_value: String(getFieldValue(key) || ''),
        new_value: editValues[key] || '',
      }))
      .filter(c => c.old_value !== c.new_value);

    if (corrections.length === 0) return;

    setSaving(true);
    try {
      await api.patch(`/invoices/${selected.id}/extraction`, { corrections });
      await loadInvoice(selected.id);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const hasEdits = Object.values(editing).some(Boolean);

  const handleAction = async (action) => {
    if (!selected) return;
    try {
      if (action === 'reject') {
        await api.post(`/invoices/${selected.id}/reject`, { reason: 'Rejected during extraction review' });
      } else {
        await api.post(`/invoices/${selected.id}/${action}`);
      }
      setInvoices(prev => prev.filter(i => i.id !== selected.id));
      setSelected(null);
      setEditing({});
      setEditValues({});
      setFeedbackHistory([]);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="extraction-review-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">AI Extraction Review</h1>
        <p className="text-sm text-slate-500 mt-1">Review, correct and confirm AI-extracted invoice data</p>
      </div>

      {/* Invoice selector */}
      {invoices.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {invoices.map(inv => (
            <Button
              key={inv.id}
              variant={selected?.id === inv.id ? "default" : "outline"}
              size="sm"
              onClick={() => loadInvoice(inv.id)}
              data-testid={`select-invoice-${inv.invoice_number}`}
              className={`rounded-sm text-xs whitespace-nowrap ${selected?.id === inv.id ? 'bg-[#0A2463] text-white' : ''}`}
            >
              {inv.invoice_number}
            </Button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-slate-200 rounded-sm h-[calc(100vh-16rem)]">
          <Skeleton className="h-full" />
          <Skeleton className="h-full" />
        </div>
      ) : !selected ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-24 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
            <p className="mt-4 text-sm text-slate-400">No invoices pending extraction review</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-slate-200 rounded-sm overflow-hidden bg-white h-[calc(100vh-16rem)]" data-testid="split-screen">
          {/* Left: PDF Viewer */}
          <div className="border-r border-slate-200 bg-slate-50 flex flex-col">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2">
              <FileText className="h-3 w-3 text-slate-400" />
              <span className="text-xs font-medium text-slate-600">{selected.original_filename || `${selected.invoice_number}.pdf`}</span>
              {selected.document_type && (
                <Badge variant="outline" className="ml-auto rounded-sm text-[9px] uppercase">{selected.document_type}</Badge>
              )}
            </div>
            <PdfPreview invoiceId={selected.id} />
          </div>

          {/* Right: Extracted Fields with Tabs */}
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600">Extraction Review</span>
              <Badge variant="outline" className={`rounded-sm text-[10px] ${CONFIDENCE_BADGES[selected.confidence] || ''}`}>
                {selected.confidence || 'N/A'} confidence
              </Badge>
            </div>

            <Tabs defaultValue="fields" className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 pt-2">
                <TabsList className="w-full rounded-sm h-8 bg-slate-100 p-0.5">
                  <TabsTrigger value="fields" className="rounded-sm text-xs flex-1" data-testid="tab-fields">
                    Extracted Fields
                  </TabsTrigger>
                  <TabsTrigger value="feedback" className="rounded-sm text-xs flex-1" data-testid="tab-feedback">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Feedback ({feedbackHistory.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="fields" className="flex-1 overflow-y-auto m-0">
                <div className="p-5 space-y-3">
                  {EDITABLE_FIELDS.map(({ key, label }) => {
                    const value = getFieldValue(key);
                    const isEditing = editing[key];

                    return (
                      <div key={key} className="space-y-1" data-testid={`field-${key}`}>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</p>
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <Input
                                value={editValues[key] || ''}
                                onChange={(e) => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                                className="h-8 text-sm font-mono rounded-sm border-[#007AFF] focus:ring-[#007AFF]"
                                data-testid={`input-${key}`}
                              />
                              <button
                                onClick={() => cancelEdit(key)}
                                className="text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid={`cancel-edit-${key}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-sm text-slate-900 font-mono flex-1 px-2 py-1 bg-slate-50 border border-slate-100 rounded-sm truncate">
                                {value || '\u2014'}
                              </span>
                              <button
                                onClick={() => startEdit(key)}
                                className="text-slate-300 hover:text-[#007AFF] transition-colors"
                                data-testid={`edit-${key}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {hasEdits && (
                    <Button
                      onClick={saveCorrections}
                      disabled={saving}
                      data-testid="save-corrections-button"
                      className="w-full rounded-sm bg-[#007AFF] hover:bg-[#007AFF]/90 text-white mt-2"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {saving ? 'Saving...' : 'Save Corrections'}
                    </Button>
                  )}

                  <Separator />
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Line Items</p>
                  {(selected.line_items || []).map(li => (
                    <div key={li.id} className="p-3 border border-slate-100 rounded-sm space-y-1">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{li.cost_type?.replace(/_/g, ' ')}</Badge>
                        <Badge variant="outline" className={`rounded-sm text-[9px] ${CONFIDENCE_BADGES[li.confidence] || ''}`}>{li.confidence}</Badge>
                      </div>
                      <p className="text-xs text-slate-600">{li.description}</p>
                      <p className="font-mono text-sm text-slate-900">{li.currency} {parseFloat(li.amount).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="feedback" className="flex-1 overflow-y-auto m-0">
                <div className="p-5 space-y-3">
                  {feedbackHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-10 w-10 mx-auto text-slate-200 stroke-[1px]" />
                      <p className="mt-3 text-sm text-slate-400">No corrections submitted yet</p>
                      <p className="text-xs text-slate-300 mt-1">Edit fields and save to create feedback records</p>
                    </div>
                  ) : (
                    feedbackHistory.map((fb, i) => (
                      <div key={fb.id || i} className="p-3 border border-slate-100 rounded-sm space-y-2" data-testid={`feedback-${i}`}>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                            {fb.field_name?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {fb.feedback_timestamp ? new Date(fb.feedback_timestamp).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">Original</p>
                            <p className="font-mono text-red-600 line-through">{fb.old_value || '\u2014'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">Corrected</p>
                            <p className="font-mono text-green-700">{fb.new_value || '\u2014'}</p>
                          </div>
                        </div>
                        {fb.corrected_by && (
                          <p className="text-[10px] text-slate-400">by {fb.corrected_by}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="p-4 border-t border-slate-200 flex gap-3">
              <Button
                onClick={() => handleAction('confirm')}
                disabled={hasEdits}
                data-testid="confirm-extraction"
                className="flex-1 rounded-sm bg-[#16A34A] hover:bg-[#16A34A]/90 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" /> Confirm
              </Button>
              <Button
                onClick={() => handleAction('flag-review')}
                variant="outline"
                data-testid="flag-review-button"
                className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <AlertTriangle className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => handleAction('reject')}
                variant="outline"
                data-testid="reject-extraction"
                className="flex-1 rounded-sm border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" /> Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
